import type { FC, ReactNode } from 'react'
import type {
    PriceItem,
    PriceTab,
    QuoteItem
} from '@/types'

import { useState } from 'react'
import { Check, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatRange } from '@/lib/format'
import { Button } from '@/components/generals/button'
import { useQuoteList } from '@/components/generals/quote-list-provider'
import { usePageTransition } from '@/components/generals/page-transition'
import { QUOTE_PATH } from '@/constants/site'

import {
    priceItems,
    priceTabs,
    defaultPriceSelection,
    PRICE_FINEPRINT
} from '@/constants/pricing'

const tabId = (tab: PriceTab): string => `price-tab-${tab.toLowerCase().replace(/[^a-z]+/g, '-')}`

export const PriceEstimator: FC = (): ReactNode => {
    const [tab, setTab] = useState<PriceTab>('Springs')
    const [selected, setSelected] = useState<string[]>(defaultPriceSelection)
    const { has, add, toggle } = useQuoteList()
    const { navigate } = usePageTransition()

    const chosen = priceItems.filter(item => selected.includes(item.id))
    const lo = chosen.reduce((sum, item) => sum + item.lo, 0)
    const hi = chosen.reduce((sum, item) => sum + item.hi, 0)

    const quoteItem: QuoteItem | null = chosen.length
        ? {
            key: `estimate:${chosen.map(item => item.id).join('+')}`,
            kind: 'estimate',
            title: chosen.length === 1 ? chosen[0].name : `Price guide selection, ${chosen.length} items`,
            detail: chosen.map(item => item.name).join(', '),
            priceLabel: formatRange(lo, hi),
            formService: chosen[0].formService,
            answers: Object.assign({}, ...chosen.map(item => item.answers ?? {}))
        }
        : null

    const added = quoteItem ? has(quoteItem.key) : false

    const toggleItem = (item: PriceItem) => {
        setSelected(current => {
            if (current.includes(item.id)) return current.filter(id => id !== item.id)

            const withoutAlternatives = item.group
                ? current.filter(id => priceItems.find(entry => entry.id === id)?.group !== item.group)
                : current

            return [...withoutAlternatives, item.id]
        })
    }

    const handleQuote = () => {
        if (quoteItem && add(quoteItem)) navigate(`${QUOTE_PATH}?entry=estimate`)
    }

    const visible = priceItems.filter(item => item.tab === tab)

    return (
        <div className='grid gap-6 lg:grid-cols-12 lg:gap-8'>
            <div className='rounded-[30px] border border-ink/[0.08] bg-card p-3 sm:p-4 lg:col-span-7'>
                <div role='tablist' aria-label="Price categories" className='grid grid-cols-2 gap-1 rounded-[22px] bg-sand/70 p-1 sm:grid-cols-4'>
                    {priceTabs.map(option => {
                        const count = priceItems.filter(item => item.tab === option && selected.includes(item.id)).length

                        return (
                            <button
                                key={option}
                                id={tabId(option)}
                                type='button'
                                role='tab'
                                aria-selected={tab === option}
                                aria-controls='price-panel'
                                onClick={() => setTab(option)}
                                className={cn(
                                    'relative flex h-11 items-center justify-center gap-2 rounded-[18px] px-3 text-[14px] font-medium transition-[background-color,color,box-shadow] duration-300',
                                    tab === option ? 'bg-card text-ink shadow-[0_6px_20px_-10px_rgba(21,23,26,0.35)]' : 'text-slate hover:text-ink'
                                )}
                            >
                                {option}

                                {count > 0 && (
                                    <span className='flex size-5 items-center justify-center rounded-full bg-ember font-mono text-[10px] text-white'>
                                        {count}
                                    </span>
                                )}
                            </button>
                        )
                    })}
                </div>

                <div id='price-panel' role='tabpanel' aria-labelledby={tabId(tab)} className='mt-3 space-y-2'>
                    {visible.map(item => {
                        const checked = selected.includes(item.id)

                        return (
                            <label
                                key={item.id}
                                className={cn(
                                    'flex cursor-pointer items-center gap-4 rounded-[20px] border px-4 py-4 transition-[background-color,border-color] duration-300 sm:px-5',
                                    checked ? 'border-ink/25 bg-ember-tint/50' : 'border-transparent hover:bg-sand/60'
                                )}
                            >
                                <input
                                    type='checkbox'
                                    checked={checked}
                                    onChange={() => toggleItem(item)}
                                    className='peer sr-only'
                                />

                                <span
                                    aria-hidden='true'
                                    className={cn(
                                        'flex size-6 shrink-0 items-center justify-center rounded-lg border transition-colors duration-300 peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-ember',
                                        checked ? 'border-ember bg-ember text-white' : 'border-ink/25 bg-card'
                                    )}
                                >
                                    <Check className={cn('size-3.5 transition-transform duration-300', checked ? 'scale-100' : 'scale-0')} />
                                </span>

                                <span className='min-w-0 flex-1'>
                                    <span className='block text-[15.5px] font-medium text-ink'>{item.name}</span>
                                    <span className='mt-0.5 block text-[13.5px] text-slate'>{item.note}</span>
                                </span>

                                <span className='shrink-0 font-mono text-[13px] text-ink'>
                                    {formatRange(item.lo, item.hi)}
                                </span>
                            </label>
                        )
                    })}
                </div>

                <p className='px-2 pb-1 pt-4 text-[12.5px] text-slate'>
                    A few choices are either-or, such as the two torsion spring options. Selecting one clears the other.
                </p>
            </div>

            <aside className='lg:col-span-5'>
                <div className='relative overflow-hidden rounded-[30px] bg-ink p-7 text-cream sm:p-8 lg:sticky lg:top-28'>
                    <div className='door-grooves pointer-events-none absolute inset-0 opacity-40' aria-hidden='true' />

                    <div className='relative'>
                        <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-cream/50'>
                            Your selection
                        </p>

                        <ul className='mt-5 space-y-3' aria-live='polite'>
                            {chosen.length === 0 && (
                                <li className='text-[15px] text-cream/60'>Choose anything that sounds like your door. The total builds here.</li>
                            )}

                            {chosen.map(item => (
                                <li key={item.id} className='flex items-baseline justify-between gap-4 border-b border-cream/10 pb-3 text-[14.5px]'>
                                    <span className='text-cream/85'>{item.name}</span>
                                    <span className='shrink-0 font-mono text-[12.5px] text-cream/70'>{formatRange(item.lo, item.hi)}</span>
                                </li>
                            ))}
                        </ul>

                        <div className='mt-7'>
                            <p className='font-mono text-[11px] uppercase tracking-[0.18em] text-cream/50'>
                                Expected range
                            </p>

                            <p
                                key={`${lo}-${hi}`}
                                className='mt-2 font-heading text-[2.6rem] font-semibold leading-none tracking-[-0.045em] animate-in fade-in slide-in-from-bottom-2 duration-500 sm:text-[3rem]'
                            >
                                {chosen.length ? formatRange(lo, hi) : '$0'}
                            </p>
                        </div>

                        <div className='mt-8 flex flex-col gap-3 sm:flex-row lg:flex-col xl:flex-row'>
                            <Button onClick={handleQuote} disabled={!quoteItem} className='flex-1 justify-between'>
                                Turn this into a quote
                            </Button>

                            {quoteItem && (
                                <button
                                    type='button'
                                    onClick={() => toggle(quoteItem)}
                                    aria-pressed={added}
                                    className={cn(
                                        'inline-flex h-12 items-center justify-center gap-2 rounded-full px-5 text-[15px] font-medium transition-colors duration-300',
                                        added ? 'bg-cream text-ink' : 'border border-cream/25 text-cream hover:border-cream/60'
                                    )}
                                >
                                    {added ? <Check className='size-4' aria-hidden='true' /> : <Plus className='size-4' aria-hidden='true' />}
                                    {added ? "On your list" : "Add to list"}
                                </button>
                            )}
                        </div>

                        <p className='mt-6 text-[12.5px] leading-relaxed text-cream/45'>
                            {PRICE_FINEPRINT}
                        </p>
                    </div>
                </div>
            </aside>
        </div>
    )
}
